import { httpAuth } from 'config/apiClient'
import endpoints from 'constants/endpoints'
import { RootState } from 'constants/interfaces'
import { AnyAction } from 'redux'
import { ThunkAction } from 'redux-thunk'
import { IPresetPrompt, IPrompt } from 'redux/reducers/promts'

export const actionTypes = {
  SET_PROMPTS: '[PROMPTS] SET_PROMPTS',
  UPDATE_PROMPT: '[PROMPTS] UPDATE_PROMPT',
  SET_IS_PROMPTS_LOADING: '[PROMPTS] SET_IS_PROMPTS_LOADING',
  SET_IS_PROMPT_LOADING: '[PROMPTS] SET_IS_PROMPT_LOADING',
}

export const setPrompts = (prompts: IPresetPrompt[]) => ({
  type: actionTypes.SET_PROMPTS,
  payload: prompts,
})

export const setIsPromptsLoading = (isLoading: boolean) => ({
  type: actionTypes.SET_IS_PROMPTS_LOADING,
  payload: isLoading,
})

export const PROMPT_TYPES = {
  CUSTOM: 0,
  PRESET: 1,
  CONFIG: 2,
}

export const getPrompts =
  (): ThunkAction<Promise<IPresetPrompt[]>, RootState, unknown, AnyAction> => async (dispatch) => {
    dispatch(setIsPromptsLoading(true))
    try {
      const { data } = await httpAuth.get(endpoints.presetPrompts)
      if (data) {
        dispatch(setPrompts(data))
      }
      return data
    } catch (error) {
      console.error('Failed to get prompts:', error)
      return []
    } finally {
      dispatch(setIsPromptsLoading(false))
    }
  }

export const getPrompt =
  ({ prompt_id }: { prompt_id: string }): ThunkAction<Promise<string>, RootState, unknown, AnyAction> =>
  async () => {
    try {
      const { data } = await httpAuth.get(endpoints.prompt, {
        params: {
          prompt_id,
        },
      })
      return data.prompt
    } catch (error) {
      console.error('Failed to get prompt:', error)
      return null
    }
  }

export const createPrompt =
  (prompt: IPrompt): ThunkAction<Promise<string>, RootState, unknown, AnyAction> =>
  async () => {
    const { data } = await httpAuth.post(endpoints.prompt, prompt)
    if (!data?.prompt_id) {
      throw new Error('Invalid response from server: missing prompt_id')
    }
    return data.prompt_id
  }

export interface ILLMInteractionRequest {
  prompt_id: string
  message: string
  images?: string[]
  reference_images?: string[]
  message_id?: string
}

// POST /gpt/llm_interaction — returns raw LLM response string for parsing
export const callLLMInteraction =
  (payload: ILLMInteractionRequest): ThunkAction<Promise<string | null>, RootState, unknown, AnyAction> =>
  async () => {
    try {
      const { data } = await httpAuth.post(endpoints.llmInteraction, payload)
      return data?.llm_response ?? null
    } catch (error) {
      console.error('Failed to call LLM interaction:', error)
      return null
    }
  }

interface IUpdatePrompt extends Omit<IPrompt, 'is_preset_prompt'> {
  prompt_id: string
}

export const updatePrompt =
  (prompt: IUpdatePrompt): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { prompts } = getState().prompts

    for (const p of prompts) {
      if (p.oid === prompt.prompt_id) {
        p.title = prompt.data.title || ''
        p.content = prompt.data.content || ''
      }
    }
    try {
      dispatch(setPrompts(prompts))
      await httpAuth.put(endpoints.prompt, prompt)
      return true
    } catch (error) {
      console.error('Failed to update prompt:', error)
      return false
    }
  }
